import React from 'react';
import { Modal, Flex, Typography, Progress } from 'antd';
import { LogoutOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  formatLogoutCountdown,
  SUBSCRIPTION_LOGOUT_GRACE_SECONDS,
} from '../utils/subscriptionPlan';

const headerGradient =
  'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 45%, #fff1f0 100%)';

const iconWrapStyle = {
  width: 56,
  height: 56,
  borderRadius: 16,
  background: 'linear-gradient(135deg, #fa541c 0%, #fa8c16 55%, #ffc069 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 26,
  flexShrink: 0,
  boxShadow: '0 8px 20px rgba(250, 84, 28, 0.35)',
};

export default function SubscriptionExpiredLogoutModal({
  open,
  secondsRemaining,
}) {
  const countdown = formatLogoutCountdown(secondsRemaining);
  const totalSeconds = SUBSCRIPTION_LOGOUT_GRACE_SECONDS;
  const remainingPercent =
    secondsRemaining != null && totalSeconds > 0
      ? Math.max(0, Math.min(100, Math.round((secondsRemaining / totalSeconds) * 100)))
      : 0;

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={420}
      styles={{
        mask: {
          backdropFilter: 'blur(6px)',
          background: 'rgba(15, 23, 42, 0.55)',
        },
        content: {
          padding: 0,
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.18)',
        },
        body: { padding: 0 },
      }}
    >
      <div style={{ background: headerGradient, padding: '28px 28px 20px' }}>
        <Flex align="center" gap={16}>
          <div style={iconWrapStyle}>
            <LogoutOutlined />
          </div>
          <Flex vertical gap={4}>
            <Typography.Text
              strong
              style={{ fontSize: 20, letterSpacing: '-0.02em', color: '#873800' }}
            >
              Session ending
            </Typography.Text>
            <Typography.Text style={{ fontSize: 13, color: 'rgba(135, 56, 0, 0.75)' }}>
              Subscription has expired for this company
            </Typography.Text>
          </Flex>
        </Flex>
      </div>

      <Flex vertical align="center" gap={20} style={{ padding: '28px 32px 32px' }}>
        <Typography.Paragraph
          style={{
            margin: 0,
            textAlign: 'center',
            fontSize: 14,
            lineHeight: 1.6,
            color: 'rgba(0, 0, 0, 0.65)',
            maxWidth: 320,
          }}
        >
          Your company subscription has expired and been recorded. For security, you will be
          signed out automatically when the timer reaches zero.
        </Typography.Paragraph>

        <Progress
          type="circle"
          percent={remainingPercent}
          size={148}
          strokeColor={{
            '0%': '#fa541c',
            '100%': '#ffc069',
          }}
          trailColor="rgba(250, 140, 22, 0.12)"
          strokeWidth={8}
          format={() => (
            <Flex vertical align="center" gap={2}>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                Logging out
              </Typography.Text>
              <Typography.Text
                strong
                style={{
                  fontSize: 32,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  color: '#d4380d',
                  letterSpacing: '-0.02em',
                }}
              >
                {countdown}
              </Typography.Text>
            </Flex>
          )}
        />

        <Flex
          align="center"
          gap={8}
          style={{
            padding: '10px 16px',
            borderRadius: 999,
            background: 'rgba(250, 140, 22, 0.08)',
            border: '1px solid rgba(250, 140, 22, 0.18)',
          }}
        >
          <ClockCircleOutlined style={{ color: '#fa8c16' }} />
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Please save any work — logout is automatic
          </Typography.Text>
        </Flex>
      </Flex>
    </Modal>
  );
}
